import { useEffect, useState } from 'react'
import { Calendar, Car, Wrench, Pencil, Check, Trash2, X } from 'lucide-react'
import api from '../services/api'
import { Linha } from './LancamentoDepositos'

interface Registro {
  num: number; dataErro: string; placa: string; frota: string; data: string; hora: number; nro: string; posto: string; postoNome: string
  uf: string; ctf: number; motorista: string; hodometro: number; litros: number; valor: number
  erroVeiculo: boolean; erroPosto: boolean; erroHodometro: boolean; erroDuplicado: boolean; erroSaldo: boolean; combustivel: string
  pendencias: string[]
}
interface Edicao { placa: string; data: string; hora: string; posto: string; hodometro: string }

const fmtN = (v: number, c: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const br = (d: string) => (d && !d.startsWith('0001') ? d.split('-').reverse().join('/') : '—')
const fmtHora = (h: number) => { const [hh, mm = '00'] = Number(h || 0).toFixed(2).split('.'); return `${hh.padStart(2, '0')}:${mm}` }
// Nome completo do combustível pela letra (dpsdesqfr / dpserrcom).
const COMBUSTIVEL: Record<string, string> = {
  A: 'Diesel S500', B: 'Diesel S10', C: 'Diesel S50', D: 'Gasolina Comum', E: 'Gasolina Aditivada', F: 'Gasolina Premium',
  G: 'Etanol Comum', H: 'Etanol Aditivado', I: 'ARLA 32', J: 'GNV', K: 'Querosene', W: 'Óleo Lubrificante 15W40',
}
const hojeIso = () => new Date().toISOString().slice(0, 10)
const menos30 = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10) }

// Corrige Lançamentos de Combustíveis por Importação (GeneXus WDPS056W) — registros da dpserr que a importação do CTA não conseguiu gravar.
export default function CorrigeLancamentosImportacao() {
  const [dataIni, setDataIni] = useState(menos30())
  const [dataFim, setDataFim] = useState(hojeIso())
  const [frota, setFrota] = useState('')
  const [lista, setLista] = useState<Registro[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [editando, setEditando] = useState<number | null>(null)
  const [ed, setEd] = useState<Edicao>({ placa: '', data: '', hora: '', posto: '', hodometro: '' })
  const [ocupado, setOcupado] = useState<number | null>(null)

  async function carregar() {
    setErro(''); setCarregando(true)
    try { setLista((await api.get('/combustivel/cta/incorretos', { params: { dataIni, dataFim, frota } })).data) }
    catch (e: any) { setLista(null); setErro(e?.response?.data?.error || 'Erro ao listar registros.') }
    finally { setCarregando(false) }
  }
  useEffect(() => { carregar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function abrirEdicao(r: Registro) {
    setEditando(r.num); setAviso(''); setErro('')
    setEd({ placa: r.placa, data: r.data.startsWith('0001') ? '' : r.data, hora: fmtHora(r.hora), posto: r.posto, hodometro: String(r.hodometro || '') })
  }

  async function acao(r: Registro, tipo: 'corrigir' | 'gravar' | 'excluir') {
    const msgs = {
      gravar: `Gravar o abastecimento ${r.nro || r.num} (${r.placa}) com baixa no estoque e tirar da lista?`,
      excluir: `Excluir o registro ${r.num} da lista SEM gravar o abastecimento?`,
    } as const
    if (tipo !== 'corrigir' && !window.confirm(msgs[tipo])) return
    setErro(''); setAviso(''); setOcupado(r.num)
    try {
      if (tipo === 'corrigir') {
        const [hh, mm] = ed.hora.split(':')
        const res = (await api.put(`/combustivel/cta/incorretos/${r.num}`, {
          placa: ed.placa, data: ed.data || undefined, hora: ed.hora ? Number(`${Number(hh) || 0}.${(mm || '00').padStart(2, '0')}`) : undefined,
          posto: ed.posto, hodometro: ed.hodometro ? Number(ed.hodometro) : undefined,
        })).data
        setEditando(null)
        setAviso(res.erros.length || res.duplicado ? `Corrigido, mas ainda com pendência: ${[...res.erros, res.duplicado ? 'possível duplicado' : ''].filter(Boolean).join(' • ')}` : 'Corrigido — sem pendências, pode gravar.')
      } else if (tipo === 'excluir') {
        await api.delete(`/combustivel/cta/incorretos/${r.num}`)
        setAviso(`Registro ${r.num} excluído.`)
      } else {
        const res = (await api.post(`/combustivel/cta/incorretos/${r.num}/gravar`, {})).data
        setAviso(`Abastecimento gravado${res.ordnum ? ` (OS ${res.ordnum})` : ''}.`)
      }
      await carregar()
    } catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao processar o registro.') }
    finally { setOcupado(null) }
  }

  const th: React.CSSProperties = { padding: '0.4rem 0.45rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0 }
  const td: React.CSSProperties = { padding: '0.28rem 0.45rem', fontSize: '0.74rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
  const dir: React.CSSProperties = { ...td, textAlign: 'right' }
  const inp: React.CSSProperties = { fontSize: '0.74rem', padding: '1px 4px', height: 24 }
  const flag = (v: boolean) => <td style={{ ...td, textAlign: 'center', fontWeight: 800, color: v ? '#b91c1c' : '#94a3b8' }}>{v ? 'S' : 'N'}</td>
  const btn = (title: string, cor: string, icon: React.ReactNode, onClick: () => void, disabled = false) => (
    <button title={title} onClick={onClick} disabled={disabled || ocupado !== null}
      style={{ border: 'none', background: cor, color: '#fff', borderRadius: 5, padding: '2px 6px', marginRight: 3, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', opacity: disabled ? 0.4 : 1 }}>{icon}</button>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Wrench size={18} color="#fff" />
            </span>
            <div>
              <h2>Corrige Lançamentos por Importação</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Abastecimentos do CTA que não foram gravados por erro de veículo, posto ou hodômetro</div>
            </div>
          </div>
        </div>
        <div className="modal-corpo">
          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>
          <Linha label="Frota" icon={Car}>
            <input value={frota} onChange={e => setFrota(e.target.value)} maxLength={8} style={{ maxWidth: 110 }} placeholder="Todas" />
          </Linha>
          {aviso && <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>{aviso}</div>}
          {erro && <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={carregar} disabled={carregando}>{carregando ? 'Carregando...' : 'Consultar'}</button>
        </div>
      </div>

      {lista && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.6rem' }}>
              <b>{lista.length}</b> registro(s) • Erro V = veículo, P = posto, H = hodômetro, D = duplicado, S = saldo insuficiente •
              Ações: <Pencil size={11} /> corrigir placa/data/hora/posto/hodômetro, <Check size={11} /> gravar (com baixa no estoque; só sem pendências),
              <Trash2 size={11} /> excluir sem gravar
            </div>
            <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  <tr>{['Ações', 'Dta lanç', 'Placa', 'Frota', 'Dta Abast.', 'Hora', 'Posto', 'Nome da Praça', 'Est', 'Número', 'Combustível', 'Motorista', 'Hodômetro', 'Litros', 'Valor', 'V', 'P', 'H', 'D', 'S', 'Pendências'].map(c =>
                    <th key={c} style={{ ...th, textAlign: ['Hodômetro', 'Litros', 'Valor'].includes(c) ? 'right' : ['V', 'P', 'H', 'D', 'S'].includes(c) ? 'center' : 'left' }}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {lista.map((r, k) => {
                    const ed1 = editando === r.num
                    return (
                      <tr key={r.num} style={{ background: ed1 ? '#fffbeb' : k % 2 ? '#f5f9fd' : '#fff' }}>
                        <td style={td}>
                          {ed1 ? (<>
                            {btn('Salvar correção e revalidar', '#1d4ed8', <Check size={13} />, () => acao(r, 'corrigir'))}
                            {btn('Cancelar', '#64748b', <X size={13} />, () => setEditando(null))}
                          </>) : (<>
                            {btn('Corrigir', '#1d4ed8', <Pencil size={13} />, () => abrirEdicao(r))}
                            {btn('Gravar (com baixa no estoque)', '#15803d', <Check size={13} />, () => acao(r, 'gravar'), r.pendencias.length > 0 || r.erroDuplicado)}
                            {btn('Excluir sem gravar', '#b91c1c', <Trash2 size={13} />, () => acao(r, 'excluir'))}
                          </>)}
                        </td>
                        <td style={td}>{br(r.dataErro)}</td>
                        <td style={td}>{ed1 ? <input style={{ ...inp, width: 80 }} maxLength={8} value={ed.placa} onChange={e => setEd({ ...ed, placa: e.target.value.toUpperCase() })} /> : r.placa}</td>
                        <td style={{ ...td, fontWeight: 700, color: '#1a6ea8' }}>{r.frota}</td>
                        <td style={td}>{ed1 ? <input type="date" style={{ ...inp, width: 125 }} value={ed.data} onChange={e => setEd({ ...ed, data: e.target.value })} /> : br(r.data)}</td>
                        <td style={td}>{ed1 ? <input type="time" style={{ ...inp, width: 80 }} value={ed.hora} onChange={e => setEd({ ...ed, hora: e.target.value })} /> : fmtHora(r.hora)}</td>
                        <td style={{ ...td, color: '#b91c1c' }}>{ed1 ? <input style={{ ...inp, width: 60 }} maxLength={4} value={ed.posto} onChange={e => setEd({ ...ed, posto: e.target.value.toUpperCase() })} /> : r.posto}</td>
                        <td style={td}>{r.postoNome}</td>
                        <td style={td}>{r.uf}</td>
                        <td style={td}>{r.nro}</td>
                        <td style={td}>{COMBUSTIVEL[r.combustivel] ?? (r.combustivel || '—')}</td>
                        <td style={td}>{r.motorista}</td>
                        <td style={dir}>{ed1 ? <input style={{ ...inp, width: 80, textAlign: 'right' }} maxLength={7} value={ed.hodometro} onChange={e => setEd({ ...ed, hodometro: e.target.value.replace(/\D/g, '') })} /> : fmtN(r.hodometro, 0)}</td>
                        <td style={dir}>{fmtN(r.litros, 3)}</td>
                        <td style={dir}>{fmtN(r.valor, 2)}</td>
                        {flag(r.erroVeiculo)}{flag(r.erroPosto)}{flag(r.erroHodometro)}{flag(r.erroDuplicado)}{flag(r.erroSaldo)}
                        <td style={{ ...td, whiteSpace: 'normal', minWidth: 240, color: r.pendencias.length ? '#b91c1c' : '#15803d', fontWeight: 600 }}>{r.pendencias.length ? r.pendencias.join(' • ') : 'Sem pendências'}</td>
                      </tr>
                    )
                  })}
                  {lista.length === 0 && <tr><td colSpan={21} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum registro pendente no período.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
